import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Trophy, Loader2, Megaphone, Clock, BarChart3,
} from 'lucide-react'
import { tournamentsAPI, eventsAPI, lanEditionsAPI, pollsAPI } from '@/lib/api'
import KassaHomeWidget from '@/components/kassa/KassaHomeWidget'
import RaffleHomeWidget from '@/components/RaffleHomeWidget'
import LiveNowHero from '@/components/LiveNowHero'
import HomeQuickStrip from '@/components/HomeQuickStrip'
import VoorMij from '@/components/VoorMij'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'

/* Tikkende klok. Simpele hook, updates elke 30s (genoeg voor HH:MM). */
function useTicker(intervalMs = 30000) {
  const [tick, setTick] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setTick(new Date()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return tick
}

function formatDate(d) {
  return d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' })
}
function formatClock(d) {
  return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })
}
function formatRelative(dateString) {
  if (!dateString) return ''
  const d = new Date(dateString)
  const s = Math.floor((Date.now() - d) / 1000)
  if (s < 60) return 'nu'
  if (s < 3600) return `${Math.floor(s / 60)}m geleden`
  if (s < 86400) return `${Math.floor(s / 3600)}u geleden`
  if (s < 604800) return `${Math.floor(s / 86400)}d geleden`
  return d.toLocaleDateString('nl-BE')
}

export default function HomePage() {
  const now = useTicker()

  const [announcements, setAnnouncements] = useState([])
  const [activeEdition, setActiveEdition] = useState(null)
  const [activeTournaments, setActiveTournaments] = useState(0)
  const [activePolls, setActivePolls] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    Promise.allSettled([
      tournamentsAPI.getTournaments(),
      eventsAPI.getAllPages({ page_type: 'announcement' }),
      lanEditionsAPI.getActive(),
      pollsAPI.getPolls({ activeOnly: 'true' }),
    ]).then(([tr, ar, er, pr]) => {
      if (!alive) return
      if (tr.status === 'fulfilled') {
        const list = tr.value.data.tournaments || []
        setActiveTournaments(list.filter(t => t.status === 'in_progress').length)
      }
      if (ar.status === 'fulfilled') {
        setAnnouncements((ar.value.data.pages || []).slice(0, 4))
      }
      if (er.status === 'fulfilled') {
        setActiveEdition(er.value.data.edition)
      }
      if (pr.status === 'fulfilled') {
        setActivePolls((pr.value.data.polls || pr.value.data || []).slice(0, 4))
      }
      setLoading(false)
    })
    return () => { alive = false }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-muted-foreground py-24 justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-acid" />
        <span>Laden…</span>
      </div>
    )
  }

  const primaryAnnouncement = announcements[0]
  const restAnnouncements = announcements.slice(1)

  /* Live-strip subtitle — alleen invullen als er iets specifieks aan het
     tournament-front leeft. LiveNowHero-tegels tonen ALS de echte staat
     (shows/compo's/kassa); dubbelop hier zou vaak fout uitkomen. */
  const liveMsg = activeTournaments > 0
    ? `${activeTournaments} toernooi${activeTournaments === 1 ? '' : 'en'} bezig`
    : ''

  return (
    <div className="space-y-6">
      {/* Eyebrow met datum + LAN-editie boven VoorMij. Kop "Wat er nu speelt"
          is weggehaald — VoorMij ("Voor {naam}") is de eigenlijke landing. */}
      <div className="eyebrow px-1">
        {activeEdition?.name ? `${formatDate(now)} · ${activeEdition.name}` : formatDate(now)}
      </div>

      {/* Voor mij — persoonlijke tijdlijn is de kop van deze pagina */}
      <VoorMij />

      {/* Snelle info: volgende programma-item, jouw volgende match,
          raffle-tickets, online spelers. Tegels verbergen zichzelf als er
          niks te tonen valt; hele strip verdwijnt bij lege response. */}
      <HomeQuickStrip />

      {/* Op de LAN — één live-blok. Kabinet-strip (Nu-dot + status + klok)
          is samengevoegd met LiveNowHero-header zodat er niet drie live-
          signalen om aandacht vechten. */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-2 px-1">
          <div className="flex items-center gap-3">
            <span className="live-dot">Nu</span>
            <span className="eyebrow">
              Op de LAN
            </span>
            {liveMsg && <span className="text-sm text-muted-foreground">{liveMsg}</span>}
          </div>
          <span className="font-mono text-sm text-muted-foreground tabular-nums">{formatClock(now)}</span>
        </div>
        <LiveNowHero />
      </div>

      {/* Van de organisator */}
      {announcements.length > 0 && (
        <section>
          <SectionHeading icon={Megaphone} title="Van de organisator" link={{ to: '/events', label: 'Archief' }} />
          <div className="grid gap-3 lg:grid-cols-3">
            {primaryAnnouncement && (
              <Link
                to="/events"
                className="lg:col-span-2 rounded-md border border-border bg-card/60 hover:border-acid/50 transition-colors p-5 flex flex-col gap-2 no-underline"
                style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}
              >
                <div className="font-mono text-[11px] uppercase tracking-widest text-peach">Belangrijk</div>
                <h3 className="text-lg md:text-xl font-semibold tracking-tight leading-snug">{primaryAnnouncement.title}</h3>
                {primaryAnnouncement.content && (
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {stripHtml(primaryAnnouncement.content).slice(0, 220)}…
                  </p>
                )}
                <div className="mt-auto font-mono text-[11px] tracking-wide text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />{formatRelative(primaryAnnouncement.created_at)}
                </div>
              </Link>
            )}
            {restAnnouncements.length > 0 && (
              <div className="flex flex-col gap-2">
                {restAnnouncements.map(a => (
                  <Link
                    key={a.id}
                    to="/events"
                    className="rounded-md border border-border bg-card/60 hover:border-acid/40 transition-colors p-3 no-underline block"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-peach mb-0.5">Nieuws</div>
                    <div className="font-medium text-sm truncate">{a.title}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Clock className="h-3 w-3" />{formatRelative(a.created_at)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Actieve polls — snelle link naar stemmen. Verbergt zichzelf als
          er niks lopende is; anders compacte lijst met stem-CTA. */}
      {activePolls.length > 0 && (
        <section>
          <SectionHeading icon={BarChart3} title="Actieve polls" link={{ to: '/polls', label: 'Alle polls' }} />
          <div className="grid gap-2 md:grid-cols-2">
            {activePolls.map(p => {
              const total = (p.options || []).reduce((s, o) => s + (o.vote_count || o.votes || 0), 0)
              return (
                <Link key={p.id} to="/polls"
                      className="rounded-md border border-border bg-card/60 hover:border-acid/50 transition-colors p-3 no-underline block">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-0.5">
                    {p.user_has_voted ? 'Gestemd' : 'Stem nu'}
                  </div>
                  <div className="font-semibold text-sm truncate">{p.question || p.title}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span>{total} {total === 1 ? 'stem' : 'stemmen'}</span>
                    {p.ends_at && <span>· sluit {formatRelative(p.ends_at)}</span>}
                  </div>
                </Link>
              )
            })}
          </div>
        </section>
      )}

      {/* Bar & Raffle — twee compacte kaartjes naast elkaar onderaan */}
      <div className="grid gap-3 md:grid-cols-2 max-w-2xl">
        <KassaHomeWidget />
        <RaffleHomeWidget />
      </div>
    </div>
  )
}

function SectionHeading({ icon: Icon, title, link }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-[15px] font-semibold tracking-tight m-0">{title}</h2>
      </div>
      {link && (
        link.to
          ? <Link to={link.to} className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-acid transition-colors">{link.label}</Link>
          : <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{link.label}</span>
      )}
    </div>
  )
}

function stripHtml(html) {
  if (!html) return ''
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  } catch { return String(html) }
}
